/**
 * Zustand, Filter und Tasten der Lesewerkzeuge (#2267). Die Komponenten
 * dazu stehen in components/AnnotationTools.tsx.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  anchorElement,
  authorKey,
  orderByDocument,
  type AnnotationEntry,
} from '../utils/annotationEntries'

export type SideMode = 'space' | 'roles'

export interface AnnotationToolsState {
  /** Autor-Filter (authorKey) oder null für alle. */
  author: string | null
  setAuthor: (key: string | null) => void
  sideMode: SideMode
  setSideMode: (mode: SideMode) => void
  panelOpen: boolean
  setPanelOpen: (open: boolean) => void
}

const SIDE_KEY = 'content_annot_sides'

export function useAnnotationTools(): AnnotationToolsState {
  const [author, setAuthor] = useState<string | null>(null)
  const [panelOpen, setPanelOpen] = useState(false)
  const [sideMode, setSideModeState] = useState<SideMode>(() => {
    try { return localStorage.getItem(SIDE_KEY) === 'roles' ? 'roles' : 'space' } catch { return 'space' }
  })
  const setSideMode = useCallback((mode: SideMode) => {
    setSideModeState(mode)
    try { localStorage.setItem(SIDE_KEY, mode) } catch { /* privat */ }
  }, [])
  return { author, setAuthor, sideMode, setSideMode, panelOpen, setPanelOpen }
}

/** Einträge, die der Filter übrig lässt. */
export function filterEntries(entries: AnnotationEntry[], author: string | null): AnnotationEntry[] {
  return author ? entries.filter((e) => authorKey(e) === author) : entries
}

/** Springt zur Markierung und aktiviert ihre Karte, falls sie eine hat. */
export function goToEntry(root: HTMLElement | null, id: number | string): boolean {
  const el = anchorElement(root, id)
  if (!el) return false
  el.scrollIntoView({ block: 'center', behavior: 'smooth' })
  // Die Randspalte hört auf Klicks an Markierungen und hebt die Karte an.
  el.click()
  el.classList.add('annotation-mark--flash')
  window.setTimeout(() => el.classList.remove('annotation-mark--flash'), 1400)
  return true
}

interface KeysOptions {
  root: HTMLElement | null
  /** Sichtbare Einträge (nach Filter). */
  entries: AnnotationEntry[]
  /** M: aktuelle Auswahl markieren. */
  onHighlight?: () => void
  /** A: aktuelle Auswahl anmerken. */
  onAnnotate?: () => void
  enabled: boolean
}

function isTyping(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null
  if (!el) return false
  const tag = el.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable
}

export function useAnnotationKeys({ root, entries, onHighlight, onAnnotate, enabled }: KeysOptions): void {
  const cursor = useRef(-1)
  useEffect(() => {
    if (!enabled) return
    const onKey = (ev: KeyboardEvent) => {
      if (ev.metaKey || ev.ctrlKey || ev.altKey || isTyping(ev.target)) return
      const key = ev.key.toLowerCase()
      if (key === 'j' || key === 'k') {
        const ordered = orderByDocument(root, entries).filter((e) => anchorElement(root, e.id))
        if (ordered.length === 0) return
        ev.preventDefault()
        const step = key === 'j' ? 1 : -1
        cursor.current = cursor.current < 0 && step < 0
          ? ordered.length - 1
          : (cursor.current + step + ordered.length) % ordered.length
        goToEntry(root, ordered[cursor.current].id)
      } else if (key === 'm' && onHighlight) {
        if (!window.getSelection()?.toString().trim()) return
        ev.preventDefault()
        onHighlight()
      } else if (key === 'a' && onAnnotate) {
        if (!window.getSelection()?.toString().trim()) return
        ev.preventDefault()
        onAnnotate()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [root, entries, onHighlight, onAnnotate, enabled])
}
